import { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { parseISO } from 'date-fns';
import { useStores } from '@/hooks';
import { BookingForm, getBookingPostUuid } from '@/features/bookings';
import type { BookingFormSubmitData, BookingFormFieldName } from '@/features/bookings';
import { AppTag } from '@pro-sto/ui';
import type { UpdateAdminBookingDto } from '@pro-sto/api-client';
import type { AdminDetailedBooking } from '@/types/bookings';

interface EditBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingUuid: string;
  onUpdate: () => void;
}

const EditBookingModal: React.FC<EditBookingModalProps> = observer(({
  isOpen,
  onClose,
  bookingUuid,
  onUpdate,
}) => {
  const { bookingsStore, toastStore } = useStores();

  const [booking, setBooking] = useState<AdminDetailedBooking | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Загрузка данных бронирования
  const loadBookingData = useCallback(async () => {
    setIsLoading(true);
    try {
      await bookingsStore.fetchBookingDetails(bookingUuid);
      const bookingData = bookingsStore.selectedBooking;
      if (bookingData) {
        setBooking(bookingData);
      }
    } catch (error) {
      console.error('Ошибка загрузки данных бронирования:', error);
      toastStore.showError('Не удалось загрузить данные заказа');
    } finally {
      setIsLoading(false);
    }
  }, [bookingUuid, bookingsStore, toastStore]);

  useEffect(() => {
    if (isOpen && bookingUuid) {
      loadBookingData();
    }
  }, [isOpen, bookingUuid, loadBookingData]);

  const isFullEditAllowed = useMemo(() => {
    if (!booking) return false;
    return booking.created_by_source === 'admin_panel';
  }, [booking]);

  const readonlyFields = useMemo(() => {
    const fields = new Set<BookingFormFieldName>([
      'phone',
      'clientName',
      'licensePlate',
      'make',
      'model',
      'date',
    ]);

    if (!isFullEditAllowed) {
      fields.add('time');
      fields.add('service');
      fields.add('additionalServices');
    }

    return fields;
  }, [isFullEditAllowed]);

  // Обработчик сохранения
  const handleSubmit = useCallback(async (data: BookingFormSubmitData) => {
    if (!booking) return;

    const currentPostUuid = getBookingPostUuid(booking);
    const nextPostUuid = data.selectedPost?.value?.toString();

    const updateData: UpdateAdminBookingDto = {
      client_comment: data.comment || '',
    };

    if (nextPostUuid && nextPostUuid !== currentPostUuid) {
      updateData.post_uuid = nextPostUuid;
    }

    if (isFullEditAllowed) {
      updateData.additionalServiceUuids = data.selectedAdditionalServices.map(
        (s) => s.value!.toString(),
      );

      if (data.selectedTime) {
        const bookingDate = parseISO(booking.start_time);
        const [hours, minutes] = data.selectedTime.split(':').map(Number);
        bookingDate.setHours(hours, minutes, 0, 0);
        updateData.start_time = bookingDate.toISOString();
      }

      if (data.selectedEndTime) {
        const endDate = parseISO(booking.start_time);
        const [endHours, endMinutes] = data.selectedEndTime.split(':').map(Number);
        endDate.setHours(endHours, endMinutes, 0, 0);
        updateData.end_time = endDate.toISOString();
      }

      if (data.selectedService) {
        updateData.service_uuid = data.selectedService.value!.toString();
      }
    }

    const success = await bookingsStore.updateBooking(bookingUuid, updateData);
    if (success) {
      onUpdate();
      onClose();
    }
  }, [booking, bookingUuid, bookingsStore, isFullEditAllowed, onUpdate, onClose]);

  // Маппинг статусов для тегов
  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending_confirmation':
        return { label: 'Ожидает', color: 'yellow' as const };
      case 'confirmed':
        return { label: 'Подтвержден', color: 'blue' as const };
      case 'completed':
        return { label: 'Выполнен', color: 'green' as const };
      case 'cancelled':
        return { label: 'Отменен', color: 'red' as const };
      default:
        return { label: 'Неизвестен', color: 'default' as const };
    }
  };

  // Если данные ещё загружаются или нет booking - не рендерим форму
  if (isLoading || !booking) {
    if (!isOpen) return null;

    return (
      <div className="booking-form__overlay" data-testid="edit-booking-loading">
        <div className="booking-form__container">
          <div className="booking-form__loading">Загрузка...</div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusConfig(booking.status);

  return (
    <BookingForm
      isOpen={isOpen}
      onClose={onClose}
      mode="edit"
      title="Редактирование записи"
      submitLabel="Сохранить"
      headerExtra={
        <AppTag size="M" color={statusConfig.color}>
          {statusConfig.label}
        </AppTag>
      }
      onSubmit={handleSubmit}
      readonlyFields={readonlyFields}
      booking={booking}
    />
  );
});

EditBookingModal.displayName = 'EditBookingModal';

export default EditBookingModal;
